import { useCallback, useEffect, useRef, useState } from 'react'
import { apiFetch } from '@/lib/api'
import { useApiMutation } from '@/hooks/useApiFetch'
import { useUIStore } from '@/stores/uiStore'

export interface Generation {
  id: string
  originalUrl: string
  style: string
  role: string | null
  generatedUrls: string[] | null
  selectedUrl: string | null
  isFavorited: boolean
  modelId: string | null
  cost: number
  createdAt: string
}

interface Pagination {
  page: number
  pageSize: number
  total: number
  totalPages: number
  hasNext: boolean
  hasPrev: boolean
}

/** GET /api/ai/generations 응답 전체 */
interface GenerationsApiResponse {
  success: true
  data: {
    generations: Generation[]
    pagination: Pagination
    remainingCredits: number
  }
}

interface UseGenerationsReturn {
  generations: Generation[]
  pagination: Pagination | null
  remainingCredits: number
  isLoading: boolean
  favoritesOnly: boolean
  setFavoritesOnly: (v: boolean) => void
  toggleFavorite: (id: string, value: boolean) => Promise<void>
  refetch: () => void
  page: number
  setPage: (p: number) => void
}

export function useGenerations(): UseGenerationsReturn {
  const [generations, setGenerations] = useState<Generation[]>([])
  const [pagination, setPagination] = useState<Pagination | null>(null)
  const [remainingCredits, setRemainingCredits] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [favoritesOnly, setFavoritesOnly] = useState(false)
  const [page, setPage] = useState(1)
  const [fetchCount, setFetchCount] = useState(0)
  const addToast = useUIStore((s) => s.addToast)
  const mountedRef = useRef(true)

  const { trigger: favTrigger } = useApiMutation<{ data: { id: string; isFavorited: boolean } }>(
    '/api/ai/generations',
    'PATCH',
  )

  useEffect(() => {
    mountedRef.current = true
    return () => { mountedRef.current = false }
  }, [])

  useEffect(() => {
    const controller = new AbortController()
    setIsLoading(true)

    const params = new URLSearchParams({ page: String(page), pageSize: '12' })
    if (favoritesOnly) params.set('favorites', 'true')

    apiFetch<Generation[]>(`/api/ai/generations?${params}`, { signal: controller.signal })
      .then((res) => {
        if (!mountedRef.current) return
        if (res.success) {
          const full = res as unknown as GenerationsApiResponse
          setGenerations(full.data.generations)
          setPagination(full.data.pagination)
          setRemainingCredits(full.data.remainingCredits)
        }
      })
      .catch(() => {
        // AbortError 등 무시
      })
      .finally(() => {
        if (mountedRef.current && !controller.signal.aborted) setIsLoading(false)
      })

    return () => controller.abort()
  }, [page, favoritesOnly, fetchCount])

  const refetch = useCallback(() => setFetchCount((c) => c + 1), [])

  const handleSetFavoritesOnly = useCallback((v: boolean) => {
    setFavoritesOnly(v)
    setPage(1)
  }, [])

  const toggleFavorite = useCallback(async (id: string, value: boolean) => {
    try {
      await favTrigger({ isFavorited: value }, { pathOverride: `/api/ai/generations/${id}` })
      refetch()
    } catch {
      addToast({ type: 'error', message: '즐겨찾기 변경에 실패했습니다' })
    }
  }, [favTrigger, refetch, addToast])

  return {
    generations,
    pagination,
    remainingCredits,
    isLoading,
    favoritesOnly,
    setFavoritesOnly: handleSetFavoritesOnly,
    toggleFavorite,
    refetch,
    page,
    setPage,
  }
}
